/**
 * An example of what a template message will look like, built from the chosen
 * variable mapping and made-up sample data. Mirrors how the server fills the
 * placeholders (pb_hooks/lib/customer-reminders-core.js `resolveTemplateField`)
 * — keep the two in step.
 */
interface SampleJob {
  publicId: string
  numberTag: string
  deviceType: string
  fault: string
}

const SAMPLE_JOBS: SampleJob[] = [
  { publicId: 'JT9-RJ76', numberTag: '12', deviceType: 'Laptop', fault: 'Will not power on' },
  { publicId: 'JSP-2RF2', numberTag: '15', deviceType: 'Phone', fault: 'Cracked screen' },
]

const SAMPLE_CUSTOMER = { fullName: 'Sam Smith', phone: '27821234567' }

/** Purposes for several jobs get two sample jobs, the rest one. */
export function sampleJobsFor(purposeKey: string): SampleJob[] {
  return purposeKey.includes('multiple') ? SAMPLE_JOBS : SAMPLE_JOBS.slice(0, 1)
}

function joinUnique(values: string[], separator: string): string {
  return Array.from(new Set(values.filter(Boolean))).join(separator)
}

export function sampleFieldValue(field: string, jobs: SampleJob[]): string {
  const fullName = SAMPLE_CUSTOMER.fullName
  switch (field) {
    case 'customer_full_name':
      return fullName
    case 'customer_first_name':
      return fullName.split(' ')[0] ?? ''
    case 'customer_phone':
      return SAMPLE_CUSTOMER.phone
    case 'job_public_id':
      return joinUnique(jobs.map((j) => j.publicId), ', ')
    case 'job_number_tag':
      return joinUnique(jobs.map((j) => j.numberTag), ', ')
    case 'device_type':
      return joinUnique(jobs.map((j) => j.deviceType), ', ')
    case 'job_fault':
      return joinUnique(jobs.map((j) => j.fault), '; ')
    case 'job_count':
      return String(jobs.length)
    case 'job_list':
      return jobs.map((j) => `${j.deviceType} (${j.publicId})`).join(', ')
    default:
      return ''
  }
}

export interface PreviewPart {
  /** Stable React key: where the part starts in the template body. */
  key: number
  text: string
  /** 'value' = filled from a mapped field; 'unset' = placeholder with no field chosen. */
  kind: 'text' | 'value' | 'unset'
}

/** Split the template body into plain text and filled-in placeholders. */
export function renderTemplatePreview(
  body: string,
  variables: Record<string, string>,
  purposeKey: string,
): PreviewPart[] {
  const jobs = sampleJobsFor(purposeKey)
  const parts: PreviewPart[] = []
  let last = 0
  for (const match of body.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)) {
    const index = match.index ?? 0
    if (index > last) parts.push({ key: last, text: body.slice(last, index), kind: 'text' })
    const field = variables[match[1]]
    parts.push(
      field
        ? { key: index, text: sampleFieldValue(field, jobs), kind: 'value' }
        : { key: index, text: match[0], kind: 'unset' },
    )
    last = index + match[0].length
  }
  if (last < body.length) parts.push({ key: last, text: body.slice(last), kind: 'text' })
  return parts
}
